import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Clock, MailCheck } from "lucide-react";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setDone(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
           style={{ background: "linear-gradient(180deg, #0b2c47 0%, #145c8c 100%)" }}>
        <div className="absolute inset-0 opacity-30"
             style={{
               backgroundImage: "radial-gradient(circle at 20% 30%, #00b5cc 0%, transparent 40%), radial-gradient(circle at 80% 70%, #145c8c 0%, transparent 50%)"
             }} />
        <div className="relative z-10 p-12 flex flex-col justify-between text-white w-full">
          <Clock className="h-8 w-8 text-white" strokeWidth={1.5} />
          <div>
            <h1 className="text-5xl font-bold leading-tight tracking-tight" style={{ fontFamily: "Manrope" }}>
              Forgot your<br />password?
            </h1>
            <p className="mt-4 text-slate-200 max-w-md">
              Enter your email and we&apos;ll send you a link to set a new one. The link will be valid for 24 hours.
            </p>
          </div>
          <div className="text-xs text-slate-300">© Dreamtime Resorts</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-10 bg-white">
        <div className="w-full max-w-md">
          {!done ? (
            <>
              <h2 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "Manrope" }}>Reset password</h2>
              <p className="mt-2 text-sm text-slate-500">We&apos;ll email you a secure reset link.</p>

              <form className="mt-8 space-y-5" onSubmit={submit} data-testid="forgot-form">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={email} autoComplete="email"
                         onChange={(e) => setEmail(e.target.value)} required
                         className="mt-1" data-testid="forgot-email-input" />
                </div>
                {error && (
                  <div className="text-sm rounded-md px-3 py-2 bg-red-50 text-red-700 border border-red-200">
                    {error}
                  </div>
                )}
                <Button type="submit" disabled={submitting} className="w-full h-11 text-white"
                        style={{ background: "#145c8c" }}
                        data-testid="forgot-submit-button">
                  {submitting ? "Sending…" : "Send reset link"}
                </Button>
                <Link to="/login" className="block text-sm text-[#145c8c] hover:underline text-center mt-4">
                  ← Back to sign in
                </Link>
              </form>
            </>
          ) : (
            <div className="text-center py-6" data-testid="forgot-success">
              <MailCheck className="h-12 w-12 text-emerald-500 mx-auto" strokeWidth={1.5} />
              <h2 className="mt-4 text-2xl font-semibold tracking-tight" style={{ fontFamily: "Manrope" }}>
                Check your inbox
              </h2>
              <p className="mt-2 text-sm text-slate-500">
                If an account exists for <span className="font-medium text-slate-700">{email}</span>, we&apos;ve sent a password reset link. It expires in 24 hours.
              </p>
              <Link to="/login" className="inline-block mt-6 text-sm text-[#145c8c] hover:underline">← Back to sign in</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
